<!--
  @component
  Generates an SVG area shape using the `area` function from [d3-shape](https://github.com/d3/d3-shape).
 -->
<script>
	import { getContext } from "svelte";
	import { cubicOut } from "svelte/easing";
	import { tweened } from "svelte/motion";

	const { data, xGet, yGet, xScale, yScale, extents, yRange, yDomain } = getContext("LayerCake");

	/**  @type {String} [fill='#ab00d610'] The shape's fill color. This is technically optional because it comes with a default value but you'll likely want to replace it with your own color. */
	export let fill = "url(#grad)";
	export let colorScale;

	$: coordinates = $data.map((d) => [$xGet(d), $yGet(d)]);
	const tCoordinates = tweened(coordinates, { delay: 100, duration: 300, easing: cubicOut });
	$: tCoordinates.set(coordinates);

	$: path =
		"M" +
		$tCoordinates
			.map(([x, y]) => {
				return x + "," + y;
			})
			.join("L");

	let area;

	$: {
		area =
			path +
			("L" +
				$xScale($extents.x ? $extents.x[1] : 0) +
				"," +
				$yScale(0) +
				"L" +
				$xScale($extents.x ? $extents.x[0] : 0) +
				"," +
				$yScale(0) +
				"Z");
	}

	$: colorDomain = colorScale.domain();

	$: domainDecimal = $yDomain.map((d) => d / 100);

	// get the colorDomain values in range
	$: inRangeOfDomainDecimal = colorDomain.filter((val) => val >= domainDecimal[0] && val <= domainDecimal[1]);
	// concatenate the arrays, remove duplicates and sort
	$: domainForGradient = Array.from(new Set([...domainDecimal, ...inRangeOfDomainDecimal])).sort((a, b) => b - a);

	// Find the max and min of domainForGradient array
	$: minVal = Math.min(...domainForGradient);
	$: maxVal = Math.max(...domainForGradient);

	// create objects with value and percentage fields
	$: result = domainForGradient.map((value) => {
		let percentage = ((value - maxVal) / (minVal - maxVal)) * 100;
		let color = colorScale(value);
		return { value, percentage, color };
	});
</script>

<defs>
	<linearGradient id="grad" x1="0%" y1="0%" x2="0%" y2="100%">
		{#each result as { percentage, color }}
			<stop offset={`${percentage}%`} style={`stop-color:${color}; stop-opacity:1`} />
		{/each}
	</linearGradient>
</defs>
<path class="path-area" d={area} {fill} />
